import { IllustrationName } from '@pandiafi/design-system'
import { TFunction } from 'i18next'

import { ResidentAppError } from '../../common/error'
import { ResidentAppErrorCode } from '../../common/types'

export interface ResolvedError {
  message: string
  illustration: IllustrationName
  reloadButtonText?: string
  eventId?: string
  actionOverride?: {
    onClick: () => void
    buttonText: string
  }
}

const resolveEventId = (error: Error): string | undefined => {
  if (error instanceof ResidentAppError) {
    const eventId = (error.errorDetails as { eventId?: unknown } | undefined)
      ?.eventId
    return typeof eventId === 'string' ? eventId : undefined
  }
  return undefined
}

export const resolveResidentAppErrorMessage = (
  error: Error,
  t: TFunction<['errors', 'common'], undefined>
): ResolvedError => {
  if (error instanceof ResidentAppError) {
    switch (error.errorCode) {
      case ResidentAppErrorCode.NO_ACTIVE_CONTRACTS:
        return {
          message: t('other.noActiveContracts'),
          illustration: 'house-night',
        }
      case ResidentAppErrorCode.IMPERSONATION_WRITE_ACCESS_DENIED:
        return {
          message: t('common:impersonatorWriteOnlyModeError'),
          illustration: 'house-locked',
          actionOverride: {
            onClick: () => window.location.reload(),
            buttonText: t('common:back'),
          },
        }
      case ResidentAppErrorCode.MOVED_OUT_WRITE_ACCESS_DENIED:
        return {
          message: t('common:movedOutReadOnlyModeError'),
          illustration: 'house-locked',
        }
      case ResidentAppErrorCode.GATEWAY_TIMEOUT:
        return {
          message: t('errors:other.pageOutOfOrder'),
          illustration: 'house-unhappy',
          eventId: resolveEventId(error),
        }
      case ResidentAppErrorCode.CORE_TIMEOUT:
      case ResidentAppErrorCode.UPSTREAM_TIMEOUT:
        return {
          message: t('errors:other.pageLoadTimedOut'),
          illustration: 'house-confused',
          eventId: resolveEventId(error),
        }
      case ResidentAppErrorCode.STALE_CLIENT_REFRESH_REQUIRED:
        return {
          message: t('errors:other.staleClientRefreshRequired'),
          illustration: 'house-night',
          reloadButtonText: t('errors:other.staleClientRefreshRequired'),
        }
      case ResidentAppErrorCode.IDENTIFICATION_REQUIRED:
        return {
          message: t('errors:other.identificationRequired'),
          illustration: 'house-locked',
        }
      case ResidentAppErrorCode.FEATURE_ACCESS_DENIED:
        return {
          message: t('errors:other.featureAccessDenied'),
          illustration: 'house-locked',
        }
      case ResidentAppErrorCode.ERROR_UNBOUND_RESIDENT_USER:
        return {
          message: t('other.unboundResidentUser'),
          illustration: 'house-locked',
        }
    }
  }

  return {
    message: t('errors:other.genericError'),
    illustration: 'house-confused',
    eventId: resolveEventId(error),
  }
}
